import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { chevron } from "../components/Icons";
import { UserContext } from "../context/UserContext";
import helpStyle from "../styles/help.module.scss";
import { isLoggedInUserInfo } from "../utilities/AuthState";
import { APP_VERSION } from "../version";

const HELP_IMAGE = (file) => `${process.env.PUBLIC_URL}/images/help/${file}`;

const HELP_SECTIONS = [
  {
    id: "section-signin",
    title: "Signing in",
    paragraphs: [
      "Sign in with the shared login your team was given, or with your own email and password. The shared login is a common door. Your own account is yours.",
      "A personal account is the easier one to keep. You can reset a forgotten password, and anything you send comes from you.",
      "Collections stay in this browser for now. If the menu offers Create a personal account, use it. Forgot password is on the login page.",
    ],
    figures: [
      {
        src: HELP_IMAGE("login-guest-and-personal.jpg"),
        caption: "Sign in with your team login, or with your own email and password.",
        hint: "Capture the landing login form.",
      },
    ],
  },
  {
    id: "section-library",
    title: "Navigating the library",
    paragraphs: [
      "The library page shows all the current materials on MATLIB. Search for a specific material, a material type, or brand-specific materials when you are signed in to a brand. Use the filter to change the order, look at material status, and limit the price range.",
      "Click a swatch when you want a closer look: photos, specs, and the actions for that material. More like this article opens a page of similar materials.",
    ],
    figures: [
      {
        src: HELP_IMAGE("library-filters.jpg"),
        caption: "Library with the filter panel open.",
        hint: "Capture desktop library plus Filter box.",
      },
      {
        src: HELP_IMAGE("material-modal.jpg"),
        caption: "A material, opened for a closer look.",
        hint: "Capture a typical Asiatan or TecTuff modal.",
      },
    ],
  },
  {
    id: "section-material",
    title: "Material details",
    paragraphs: [
      "Thumbnails change the large photo. On a phone, swipe between photos.",
      "The side panel shows substrate, type, group, price, status, character, and finish. Price is there for reference. If your brand has attached an article, you will see Brand Info. If not, you will see sustainability notes, or TecTuff features on a TecTuff split.",
      "Similar Materials lists close matches by name, group, and character. Use it when you want neighbors of the material in front of you.",
    ],
    figures: [
      {
        src: HELP_IMAGE("brand-info-panel.jpg"),
        caption: "Brand Info when an article exists for that material.",
        hint: "Capture the Brand Info tab or list on a known article.",
      },
      {
        src: HELP_IMAGE("similar-materials.jpg"),
        caption: "Similar materials under one article.",
        hint: "Capture tectuff/raptor or another article with several matches.",
      },
    ],
  },
  {
    id: "section-download",
    title: "Download and copy",
    paragraphs: [
      "Download and Copy Card open a small tray so you can choose the format. Copy places the image or card on the clipboard, ready to paste into an email or anywhere else you are working. Download saves that same file to your computer.",
      "Single Image is the photo only, with the vendor and material name along the bottom. Use it when you just need the look.",
      "Info card is the wider card: the photo plus the specs, including price, character, finish, and notes.",
    ],
    figures: [
      {
        src: HELP_IMAGE("export-tray.jpg"),
        caption: "Download tray: Single Image or Info card.",
        hint: "Capture the modal footer tray on Download.",
      },
      {
        src: HELP_IMAGE("single-image-export.jpg"),
        caption: "Single Image: the photo, with the vendor and name.",
        hint: "Use a real Single Image JPEG from Download.",
      },
      {
        src: HELP_IMAGE("info-card-export.jpg"),
        caption: "Info card: the photo plus the specs.",
        hint: "Use a real Info card JPEG, no brand info is fine.",
      },
    ],
  },
  {
    id: "section-collection",
    title: "Collections and PDF",
    paragraphs: [
      "Star the materials you want to keep. Array view is for scanning. List edit is for reordering or removing swatches before you share.",
      "Put the materials you want your team to see first. That order is how they appear in the PDF.",
      "Add a title, an optional note, and choose whether prices print. In Array view, the dots on each card choose which photo goes into the PDF. The PDF stays for this visit, so download it if you want to keep it.",
    ],
    figures: [
      {
        src: HELP_IMAGE("collection_array.jpg"),
        caption: "Collection in Array view.",
        hint: "Capture a short collection with a few cards.",
      },
      {
        src: HELP_IMAGE("collection_pdf.jpg"),
        caption: "Collection PDF: title, note, and pricing.",
        hint: "Capture title, description, and price toggle.",
      },
    ],
  },
];

const PRIVACY_SECTIONS = [
  {
    title: "Our commitment",
    paragraphs: [
      "Sign-in details exist so you can look at materials. That is their only purpose. We do not build profiles, track you for marketing, or sell data.",
    ],
  },
  {
    title: "What login is for",
    paragraphs: [
      "Your login and brand access are a door into the library. Collections and the other tools are here to help you work. We do not treat them as a source of personal data.",
    ],
  },
  {
    title: "About the materials",
    paragraphs: [
      "Images, names, and specs are here so you can look and compare. They are a reference, not a quote or a production confirmation.",
      "Need something more specific? Write to Jesse Smith at jsmith@asiatan.com.",
      "MATLIB is here so you can spend time with the materials. Have a look, and enjoy it.",
    ],
  },
];

const HELP_NAV = [
  { id: "section-signin", label: "Sign in" },
  { id: "section-library", label: "Library" },
  { id: "section-material", label: "Materials" },
  { id: "section-download", label: "Download" },
  { id: "section-collection", label: "Collections" },
  { id: "privacy", label: "Privacy" },
];

function HelpFigure({ src, caption, hint }) {
  const [failed, setFailed] = useState(!src);

  return (
    <figure className={helpStyle.figure}>
      {src && !failed ? (
        <img src={src} alt={caption} onError={() => setFailed(true)} />
      ) : (
        <div
          className={helpStyle.figurePlaceholder}
          role="img"
          aria-label={`Screenshot needed: ${caption}`}
        >
          <span>Screenshot needed</span>
          {hint ? <small>{hint}</small> : null}
        </div>
      )}
      <figcaption>{caption}</figcaption>
    </figure>
  );
}

function Help() {
  const navigate = useNavigate();
  const { userInformation } = useContext(UserContext);
  const signedIn = isLoggedInUserInfo(userInformation);

  const handleClickScroll = (section) => {
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <div className={`content ${helpStyle.helpPage}`}>
      <div className={helpStyle.helpBackdrop} aria-hidden="true"></div>

      <div className={helpStyle.helpLayout}>
        <aside className={helpStyle.helpSidebar}>
          <div className={helpStyle.sidebarCard}>
            <p className={helpStyle.sidebarEyebrow}>Support</p>
            <h1 className={helpStyle.sidebarTitle}>MATLIB Support</h1>
            <p className={helpStyle.sidebarCopy}>
              A short guide to finding materials, saving a shortlist, and
              sending a card to your team.
            </p>

            <div className={helpStyle.sidebarNav}>
              {HELP_NAV.map((item) => (
                <button
                  key={item.id}
                  className={helpStyle.navButton}
                  onClick={() => handleClickScroll(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <button
              className={helpStyle.backButton}
              onClick={() => navigate(signedIn ? "/library" : "/")}
            >
              {chevron}
              {signedIn ? "Back to the Library" : "Back to sign in"}
            </button>
            <p className={helpStyle.supportVersion}>V{APP_VERSION}</p>
          </div>
        </aside>

        <main className={helpStyle.helpBody}>
          <section className={helpStyle.heroPanel}>
            <div>
              <p className={helpStyle.sectionEyebrow}>Start here</p>
              <h2 className={helpStyle.heroTitle}>How to use MATLIB.</h2>
              <p className={helpStyle.heroCopy}>
                MATLIB is the digital material library for Asiatan and TecTuff.
                It is simple to use, and your sign-in keeps the library private.
              </p>
              <p className={helpStyle.heroCopy}>
                If you or someone on your team needs a login, request one below.
              </p>
              <p className={helpStyle.heroCopy}>
                More materials are on the way. If you cannot find one, mention
                it in the form and we will tell you what we can.
              </p>
              <div className={helpStyle.heroActions}>
                <button
                  type="button"
                  className={helpStyle.heroButton}
                  onClick={() => navigate("/contact?request=login")}
                >
                  Request a login
                </button>
              </div>
            </div>
          </section>

          {HELP_SECTIONS.map((section) => (
            <section
              key={section.id}
              id={section.id}
              className={helpStyle.helpSection}
            >
              <h2 className={helpStyle.sectionTitle}>{section.title}</h2>
              <div className={helpStyle.sectionDivider}></div>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph} className={helpStyle.sectionParagraph}>
                  {paragraph}
                </p>
              ))}
              {section.figures?.length ? (
                <div className={helpStyle.figureGrid}>
                  {section.figures.map((figure) => (
                    <HelpFigure key={figure.caption} {...figure} />
                  ))}
                </div>
              ) : null}
            </section>
          ))}

          <div id="privacy" className={helpStyle.anchorOffset}></div>
          <div className={helpStyle.privacyHeader}>
            <p className={helpStyle.sectionEyebrow}>Privacy</p>
            <h1 className={helpStyle.privacyTitle}>Your privacy</h1>
          </div>
          <section className={helpStyle.privacyCallout} aria-label="Your privacy">
            <p className={helpStyle.privacyCalloutEyebrow}>A simple promise</p>
            <h2 className={helpStyle.privacyCalloutTitle}>
              We do not keep your information, and we do not use it.
            </h2>
            <p className={helpStyle.privacyCalloutCopy}>
              Your sign-in only opens the library. We do not profile you,
              market to you, or use your details for anything else.
            </p>
          </section>
          {PRIVACY_SECTIONS.map((section) => (
            <section key={section.title} className={helpStyle.helpSection}>
              <h2 className={helpStyle.sectionTitle}>{section.title}</h2>
              <div className={helpStyle.sectionDivider}></div>
              {section.paragraphs.map((paragraph) => {
                const email = "jsmith@asiatan.com";
                const emailIndex = paragraph.indexOf(email);
                return (
                  <p key={paragraph} className={helpStyle.sectionParagraph}>
                    {emailIndex === -1 ? (
                      paragraph
                    ) : (
                      <>
                        {paragraph.slice(0, emailIndex)}
                        <a
                          className={helpStyle.sectionLink}
                          href={`mailto:${email}`}
                        >
                          {email}
                        </a>
                        {paragraph.slice(emailIndex + email.length)}
                      </>
                    )}
                  </p>
                );
              })}
            </section>
          ))}
        </main>
      </div>
    </div>
  );
}

export default Help;
